var _dec, _dec2, _class, _temp;

/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Component, classMap, styleMap, html, nothing } from '@sick-davinci/ce-decorators';
import { style } from './SearchField.style';
import { icon_search } from '@sick-davinci/material-icons/action/search.icon';
import { icon_close } from '@sick-davinci/material-icons/navigation/close.icon';
import { DropDown } from '../drop-down/DropDown';
/**
 *  A Search Field enables the user to find words, sentences, and numbers in a collection of data. 
 *  The Search Field is a specialization of a dropdown list.
 *  But it's always editable and highlights the input in the dropdown list.
 *  However you can customize it the styling and highlighting with contentRenderer.
 *  Use the data property for setting the dropdown list.
 *  The content of the text-field displays the value instead of the slot.
 * 
 * @example
 * ```html
 *  <davinci-search-field id="search-countries" placeholder="Search for your country" onchange="console.log('change:' + event.detail)"></davinci-search-field>
 *  <script type="application/javascript">
 *    const data = ["Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Aruba","Australia","Austria","Azerbaijan",
 *    "Bahamas, The","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan","Bolivia","Bosnia and Herzegovina","Botswana","Brazil",
 *    "Brunei","Bulgaria","Burkina Faso","Burma","Burundi","Cambodia","Cameroon","Canada","Cabo Verde","Central African Republic","Chad","Chile","China",
 *    "Colombia","Comoros","Congo, Democratic Republic of the","Congo, Republic of the","Costa Rica","Cote d'Ivoire","Croatia","Cuba","Curacao","Cyprus","Czechia",
 *    "Denmark","Djibouti","Dominica","Dominican Republic","East Timor (see Timor-Leste)","Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia",
 *    "Eswatini","Ethiopia","Fiji","Finland","France","Gabon","Gambia, The","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau",
 *    "Guyana","Haiti","Holy See","Honduras","Hong Kong","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan",
 *    "Kazakhstan","Kenya","Kiribati","Korea, North","Korea, South","Kosovo","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya",
 *    "Liechtenstein","Lithuania","Luxembourg","Macau","Macedonia","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania",
 *    "Mauritius","Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Namibia","Nauru","Nepal","Netherlands","New Zealand",
 *    "Nicaragua","Niger","Nigeria","North Korea","Norway","Oman","Pakistan","Palau","Palestinian Territories","Panama","Papua New Guinea","Paraguay","Peru",
 *    "Philippines","Poland","Portugal","Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa",
 *    "San Marino","Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Sint Maarten","Slovakia","Slovenia",
 *    "Solomon Islands","Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Swaziland (See Eswatini)","Sweden","Switzerland",
 *    "Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda",
 *    "Ukraine","United Arab Emirates","United Kingdom","Uruguay","Uzbekistan","Vanuatu","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"];
 *    var searchField = document.querySelector('#search-countries');        
 *    searchField.addEventListener('input', function(event) {
 *       searchField.expanded = true;
 *       searchField.data = data.filter(function(value){ return value.toLowerCase().indexOf(event.detail.toLowerCase()) >= 0});
 *    });
 *  </script>
 *  ```
 *  ## Known Issues
 *  If you are using Angular and forms deffer the .data call in a `window.setTimeout`
 *  
 *  ## UMD specific imports for using the contentRenderer
 *  If you need the ES5 version you have to import the template-tag html method from the umd
 *  ```typescript
 *  import { html } from '@sick-davinci/basic-elements/umd'
 *  
 *  const element = document.createElement('davinci-drop-down')
 *  element.contentRenderer = (value, label) => html`label: ${label} value: ${value}`;
 *  ```
 *  
 *  in Angular you can assign the function within a template
 *  template:
 *  ```html
 *  <davinci-drop-down [contentRenderer]="renderer"></davinci-drop-down>
 *  ```
 *  component:
 *  ```typescript
 *  import { html } from '@sick-davinci/basic-elements/umd'
 *  
 *  export class Component
 *  contentRenderer(value, label) {
 *      return html`label: ${label} value: ${value}`;
 *  }
 *  ```
 * @slot {davinci-option[] | davinci-option-group[]} `davinci-option` or `davinci-option-group` elements
 */

export let SearchField = (_dec = Component({
  tag: 'davinci-search-field',
  style: [style],
  inheritStyle: true,
  dependencies: [DropDown]
}), _dec2 = Reflect.metadata("design:paramtypes", []), _dec(_class = _dec2(_class = (_temp = class SearchField extends DropDown {
  constructor() {
    super();

    this.clear = () => {
      this.internalValue = '';
      this.input.emit(this.internalValue);
    };

    this.search = () => {
      this.change.emit(this.internalValue);
    };

    this.editable = true;

    this.contentRenderer = (value, label) => html`${this._renderEntry(value, label)}`;
  }

  render() {
    return html`
    <div class="${classMap({
      container: true,
      expanded: this.expanded,
      placeholder: this.showPlaceholder
    })}">
      <input .placeholder="${this.placeholder}"
             type="text"
             .value="${this.internalValue}"
             @input="${this.onInputInput}"
             @change="${this.onInputChange}"
             @keydown="${this.onInputKeyDown}"
             @click="${this.onClickCapture}"></input>
      <div class="currentvalue"><slot name="currentvalue"></slot></div>
      ${this.renderClearIcon()}
      <davinci-icon-ref class="search" .src="${icon_search}" @click="${this.search}"></davinci-icon-ref>
      <davinci-spinner class="spinner"></davinci-spinner>
      <div class="overlaywrapper" @click="${this.onClickCapture}">
        <div class="list" style="${styleMap(this.listStyle)}" ><slot @slotchange="${this.onListSlotChange}"></slot></div>
      </div>
    </div>
    `;
  }
  /**
   * Clears the value of the search field.
   */


  renderClearIcon() {
    if (this.value) {
      return html`<davinci-icon-ref class="clear" .src="${icon_close}" @click="${this.clear}"></davinci-icon-ref>`;
    } else {
      return html`<davinci-icon-ref class="clear hide" .src="${icon_close}" @click="${this.clear}"></davinci-icon-ref>`;
    }
  }

  _renderEntry(value, label) {
    if (this.internalValue) {
      const startIndex = label.toLowerCase().indexOf(this.internalValue.toLowerCase());
      const endIndex = startIndex + this.internalValue.length;

      if (startIndex >= 0) {
        // Workaround: Use comments for removing spacing between <spans>
        return html`
          <span>${label.substring(0, startIndex)}</span><!--
          --><span style="${styleMap({
          color: '#323c43'
        })}">${label.substring(startIndex, endIndex)}</span><!--
          --><span>${label.substring(endIndex, label.length)}</span>
        `;
      } else {
        return nothing;
      }
    } else {
      return html`<span>${label ? label : value}</span>`;
    }
  }

}, _temp)) || _class) || _class);
//# sourceMappingURL=SearchField.js.map